import {
    Box,
    Button,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    TextField
} from "@mui/material";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import BasicDialog from "../../components/base/BasicDialog/BasicDialog";
import { SnackbarError } from "../../components/base/SnackbarAlert/SnackbarError";
import { SnackbarSuccess } from "../../components/base/SnackbarAlert/SnackbarSuccess";
import { useBookingCancellationReasons, useCancelBooking } from "../../services/bookingsService";
import { useLocalizedProperty } from "../../utils";

export default function BookingCancellationDialog({ open, onClose, bookingId, onSuccess }) {
    const { t } = useTranslation();
    const [selectedReasonId, setSelectedReasonId] = useState("");
    const [resonNote, setReasonNote] = useState("");
    const [error, setError] = useState("");

    const getLocalizedProperty = useLocalizedProperty();

    const { data: cancellationReasons, isLoading: isLoadingReasons } =
        useBookingCancellationReasons({
            select: (data) => data.results,
            enabled: open,
        });

    const { mutate: cancelBooking, isPending: isCancelling } = useCancelBooking();

    useEffect(() => {
        if (open) {
            setSelectedReasonId("");
            setError("");
            setReasonNote("")
        }
    }, [open]);

    const handleChangeReason = (event) => {
        setSelectedReasonId(event.target.value);
        setError("");
    };

    const handleCancel = () => {
        if (!selectedReasonId) {
            setError(t("bookings.cancellationDialog.reasonRequired"));
            return;
        }

        cancelBooking(
            { bookingId, reasonId: selectedReasonId, resonNote: resonNote},
            {
                onSuccess: () => {
                    toast.custom(
                        () => (
                            <SnackbarSuccess
                                message={t("bookings.cancellationDialog.successMessage")}
                            />
                        ),
                        {
                            position: "bottom-right",
                        },
                    );
                    onClose();
                    if (onSuccess) {
                        onSuccess();
                    }
                },
                onError: (error) => {
                    console.error("Error cancelling booking:", error);
                    toast.custom(
                        () => (
                            <SnackbarError
                                message={t("bookings.cancellationDialog.errorMessage")}
                            />
                        ),
                        {
                            position: "bottom-right",
                        },
                    );
                },
            },
        );
    };

    return (
        <BasicDialog
            open={open}
            onClose={onClose}
            title={t("bookings.cancellationDialog.title")}
            maxWidth="sm"
            showBottomCloseButton={false}
            showDialogActions={false}
        >
            <Box sx={{ p: 2 }}>
                <FormControl fullWidth error={!!error} disabled={isLoadingReasons || isCancelling}>
                    <InputLabel id="booking-cancellation-reason-label">
                        {t("bookings.cancellationDialog.reasonLabel")}
                    </InputLabel>
                    <Select
                        labelId="booking-cancellation-reason-label"
                        value={selectedReasonId}
                        label={t("bookings.cancellationDialog.reasonLabel")}
                        onChange={handleChangeReason}
                    >
                        {cancellationReasons?.map((reason) => (
                            <MenuItem key={reason.id} value={reason.id}>
                                {getLocalizedProperty(reason, "reason_name")}
                            </MenuItem>
                        ))}
                    </Select>
                    {error && <FormHelperText>{error}</FormHelperText>}
                    <TextField
                        sx={{marginTop: 2}}
                        labelId="booking-fiasco-reason-note_label"
                        value={resonNote}
                        multiline
                        rows={2}
                        label={t("bookings.cancellationDialog.reasonNoteLabel")}
                        onChange={(e)=>setReasonNote(e.target.value)}
                    />
                </FormControl>

                <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
                    <Button variant="outlined" onClick={onClose} disabled={isCancelling} fullWidth>
                        {t("bookings.cancellationDialog.cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleCancel}
                        disabled={isLoadingReasons || isCancelling}
                        fullWidth
                    >
                        {t("bookings.cancellationDialog.confirmButton")}
                    </Button>
                </Box>
            </Box>
        </BasicDialog>
    );
}
